import { useEffect, useState, useRef } from "react";
import {
  Button,
  Col,
  Divider,
  Form,
  Input,
  Modal,
  Row,
  message,
  DatePicker,
  TimePicker,
  Spin,
} from "antd";
import {
  createPatientMonitoringAPI,
  updatePatientMonitoringAPI,
} from "../../../services/patient.services";
import { getCookie } from "../../../helpers/localStorage";
import { FacilityID, Role } from "../../../constants/defaultKeys";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import RecordProgressNotes from "./RecordProgressNotes";
import { processAudioProgressApi } from "../../../services/doctor.services";
import useIsMobile from "../../../hooks/useIsMobile";
import { NURSE_IP_COMPACT_MODAL_WRAP } from "../../IpEMR/Doctor/InitialInspection/ClosePopup";
import { alignPickerPopupBelow } from "../../../helpers/alignPickerPopup";
import "./ProgressNotesPicker.css";
dayjs.extend(utc);
const { TextArea } = Input;
function AddNotes({
  patient,
  form,
  getData,
  isModalOpen,
  setIsModalOpen,
  userDetails,
}) {
  const userRole = getCookie(Role) || userDetails?.currRole;
  const facilityId = Number(getCookie(FacilityID)) || userDetails?.currFacility;
  const isMobile = useIsMobile();
  const isCompactPicker = useIsMobile(1200);
  const [startRecord, setStartRecord] = useState(true);
  const [formStep, setFormStep] = useState("record");
  const [recordedFile, setRecordedFile] = useState(null);
  const [aiResponse, setAiResponse] = useState(null);
  const [aiLoading, setAiLoading] = useState(false);

  const stopRecordingRef = useRef(null);

  useEffect(() => {
    if (!isModalOpen?.status || isModalOpen?.type !== "add") return;

    if (isCompactPicker) {
      form.setFieldsValue({
        record_date: dayjs(),
        record_time_only: dayjs(),
        record_time: undefined,
      });
    } else {
      form.setFieldsValue({
        record_time: dayjs(),
      });
    }

    if (isModalOpen?.state === "addAi") {
      form.setFieldsValue({
        notes: aiResponse?.notes,
        ...(isCompactPicker
          ? {
              record_date: dayjs(),
              record_time_only: dayjs(),
              record_time: undefined,
            }
          : { record_time: dayjs() }),
      });
    }
  }, [isModalOpen, form, aiResponse, isCompactPicker]);

  function formatRecordTime(values) {
    if (isCompactPicker) {
      return dayjs(values.record_date)
        .hour(dayjs(values.record_time_only).hour())
        .minute(dayjs(values.record_time_only).minute())
        .second(0)
        .utc()
        .format("YYYY-MM-DD HH:mm:ss");
    }

    return values.record_time.utc().format("YYYY-MM-DD HH:mm:ss");
  }

  async function onFinish(values) {
    try {
      const recordTime = formatRecordTime(values);
      const { record_date, record_time_only, record_time, ...rest } = values;

      const res =
        isModalOpen?.type === "add"
          ? await createPatientMonitoringAPI(patient, {
              role: userRole,
              facility_id: facilityId,
              ...rest,
              record_time: recordTime,
            })
          : await updatePatientMonitoringAPI(patient, isModalOpen?.id, {
              ...rest,
              record_time: recordTime,
            });
      message.success(res?.data?.message);
      setIsModalOpen({ status: false, type: "add", id: 0 });
      getData();
    } catch (error) {
      message.error(error?.response?.data?.message);
    }
  }
  const onRecordSubmit = async () => {
    if (!recordedFile) {
      message.error("No recording found");
      return;
    }

    setStartRecord(false);
    if (stopRecordingRef.current) {
      stopRecordingRef.current();
    }

    setAiLoading(true);
    try {
      const fd = new FormData();
      fd.append("audio_file", recordedFile);

      const response = await processAudioProgressApi(fd);
      setFormStep("medicine");
      setAiResponse(response?.data || {});

      message.success("Audio processed successfully!");
    } catch (error) {
      console.error(error);
      message.error("Failed to process audio.");
    } finally {
      setAiLoading(false);
    }
  };
  return (
    <Modal
      footer={null}
      title={isModalOpen?.type === "add" ? "Add Notes" : "Update Notes"}
      open={isModalOpen?.status}
      onCancel={() => setIsModalOpen({ status: false, type: "add", id: 0 })}
      centered={isMobile}
      width={isMobile ? "min(400px, calc(100vw - 32px))" : undefined}
      wrapClassName={isMobile ? NURSE_IP_COMPACT_MODAL_WRAP : undefined}
      className={isMobile ? "nurse-progress-notes-modal" : undefined}
      destroyOnClose
    >
      {aiLoading ? (
        <div style={{ textAlign: "center", padding: isMobile ? "24px 0" : "40px 0" }}>
          <Spin size="large" tip="Processing..." />
        </div>
      ) : (
        <>
          {formStep === "record" && isModalOpen?.state === "addAi" ? (
            <>
              <RecordProgressNotes
                startRecord={startRecord}
                setStartRecord={setStartRecord}
                setRecordedFile={setRecordedFile}
                stopAllRecordings={stopRecordingRef}
              />
              <div
                style={{
                  display: "flex",
                  alignItems: "right",
                  justifyContent: "right",
                }}
              >
                <Button
                  className="inv-btn"
                  type="primary"
                  onClick={onRecordSubmit}
                >
                  Submit
                </Button>
              </div>
            </>
          ) : (
            <Form
              form={form}
              name="notes"
              layout="vertical"
              onFinish={onFinish}
            >
              {!isMobile && <Divider />}
              <Row style={isMobile ? undefined : { marginTop: "18px" }}>
                <Col span={24}>
                  <Form.Item
                    label="Progress Notes"
                    name="notes"
                    rules={[
                      {
                        required: true,
                        message: "Please input progress notes!",
                      },
                    ]}
                  >
                    <TextArea
                      style={{ minHeight: "100px" }}
                      placeholder="Add Progress Notes"
                    />
                  </Form.Item>
                </Col>
                <Col span={24}>
                  <Form.Item label="Record Time" required>
                    {isCompactPicker ? (
                      <Row gutter={[8, 8]}>
                        <Col span={24}>
                          <Form.Item
                            name="record_date"
                            rules={[
                              { required: true, message: "Select date" },
                            ]}
                            noStyle
                          >
                            <DatePicker
                              style={{ width: "100%" }}
                              format="YYYY-MM-DD"
                              placement="bottomLeft"
                              getPopupContainer={() => document.body}
                              popupClassName="curengo-mobile-picker"
                              inputReadOnly
                              onOpenChange={(open) => {
                                alignPickerPopupBelow(
                                  open,
                                  ".curengo-mobile-picker.ant-picker-dropdown"
                                );
                              }}
                            />
                          </Form.Item>
                        </Col>
                        <Col span={24}>
                          <Form.Item
                            name="record_time_only"
                            rules={[
                              { required: true, message: "Select time" },
                            ]}
                            noStyle
                          >
                            <TimePicker
                              style={{ width: "100%" }}
                              format="HH:mm"
                              placement="bottomLeft"
                              getPopupContainer={() => document.body}
                              popupClassName="progress-notes-time-picker"
                              inputReadOnly
                              onOpenChange={(open) => {
                            alignPickerPopupBelow(
                              open,
                              ".progress-notes-time-picker.ant-picker-dropdown",
                              { offsetUp: 48 }
                            );
                              }}
                            />
                          </Form.Item>
                        </Col>
                      </Row>
                    ) : (
                      <Form.Item
                        name="record_time"
                        rules={[
                          {
                            required: true,
                            message: "Please select record time",
                          },
                        ]}
                        noStyle
                      >
                        <DatePicker
                          showTime={{ format: "HH:mm" }}
                          style={{ width: "34%" }}
                          format="YYYY-MM-DD HH:mm"
                          getPopupContainer={() => document.body}
                          popupClassName="progress-notes-record-time-picker"
                          onOpenChange={(open) => {
                            alignPickerPopupBelow(
                              open,
                              ".progress-notes-record-time-picker.ant-picker-dropdown",
                              { offsetUp: 72 }
                            );
                          }}
                        />
                      </Form.Item>
                    )}
                  </Form.Item>
                </Col>

                <Divider />
                <Col
                  span={24}
                  className={isMobile ? "progress-notes-modal-footer" : "flex-end"}
                >
                  <Form.Item wrapperCol={isMobile ? undefined : { span: 4 }}>
                    <Button
                      className="inv-btn"
                      type="primary"
                      htmlType="submit"
                    >
                      {isModalOpen?.type === "add" ? "Add" : "Update"}
                    </Button>
                  </Form.Item>
                </Col>
              </Row>
            </Form>
          )}
        </>
      )}
    </Modal>
  );
}

export default AddNotes;
